import axios from 'axios';
import { apolloClient } from './apollo';
import { VerifyTokenDocument } from '../graphql/generated/graphql';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_URL_API}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Nova instância para a API na porta 4090
const apiNew = axios.create({
  baseURL: `${process.env.REACT_APP_URL_API_NEST}`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ========================================
// Controle de refresh token (anti-loop)
// ========================================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const LOGOUT_MUTATION = `
  mutation Logout {
    logout
  }
`;

const callLogoutMutation = (token) => {
  if (!token) return;
  // Chama logout via fetch direto para invalidar tokens no servidor
  fetch(`${process.env.REACT_APP_URL_API_NEST}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query: LOGOUT_MUTATION }),
  }).catch(() => {
    // Ignora erros de logout - o importante é limpar o localStorage
  });
};

const forceLogout = () => {
  const token = localStorage.getItem('@AcordeOn:token');
  callLogoutMutation(token);

  localStorage.removeItem('@AcordeOn:token');
  localStorage.removeItem('@AcordeOn:refreshToken');
  localStorage.removeItem('@AcordeOn:user');
  // Redireciona para login se não estiver na página de login/signup
  if (
    !window.location.pathname.includes('/login') &&
    !window.location.pathname.includes('/cadastro') &&
    !window.location.pathname.includes('/redefinir-senha')
  ) {
    window.location.href = '/login';
  }
};

/**
 * Tenta renovar o token usando a mutation VerifyToken do GraphQL.
 * Envia o refreshToken no header Authorization para a API validar.
 * Retorna os novos tokens ou lança erro.
 */
const refreshTokenViaGraphQL = async () => {
  const refreshToken = localStorage.getItem('@AcordeOn:refreshToken');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const { data } = await apolloClient.mutate({
    mutation: VerifyTokenDocument,
    context: {
      headers: {
        authorization: `Bearer ${refreshToken}`,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const result = data?.verifyToken;

  if (!result) {
    throw new Error('Empty response from verifyToken');
  }

  // Verifica se a resposta é um erro (LoginErrorResponse)
  if (result.__typename === 'LoginErrorResponse') {
    throw new Error(result.message || 'Token refresh failed');
  }

  // Sucesso (LoginSuccessResponse)
  if (result.__typename === 'LoginSuccessResponse') {
    return {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
    };
  }

  throw new Error('Unexpected response from verifyToken');
};

/**
 * Handler compartilhado para interceptar erros 401 e tentar refresh.
 * Usa fila para evitar múltiplas chamadas simultâneas de refresh.
 */
const handle401Error = async (error, axiosInstance) => {
  const originalRequest = error.config;

  // Se não é 401 ou já tentou retry, rejeita direto
  if (!error.response || error.response.status !== 401 || originalRequest._retry) {
    return Promise.reject(error);
  }

  // Se já está fazendo refresh, enfileira a requisição
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      })
      .catch((err) => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const tokens = await refreshTokenViaGraphQL();

    // Atualiza tokens no localStorage
    localStorage.setItem('@AcordeOn:token', tokens.accessToken);
    localStorage.setItem('@AcordeOn:refreshToken', tokens.refreshToken);

    // Processa fila de requisições pendentes
    processQueue(null, tokens.accessToken);

    // Refaz a requisição original com o novo token
    originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    // Refresh falhou - faz logout e rejeita tudo
    processQueue(refreshError, null);
    console.error('Token refresh failed, logging out:', refreshError.message);
    forceLogout();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

// ========================================
// Interceptors de REQUEST (adicionar token)
// ========================================

// Interceptor para adicionar token na API antiga
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('@AcordeOn:token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para adicionar token na nova API
apiNew.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('@AcordeOn:token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ========================================
// Interceptors de RESPONSE (refresh token)
// ========================================

// Interceptor de refresh para a API antiga
api.interceptors.response.use(
  (response) => response,
  (error) => handle401Error(error, api)
);

// Interceptor de refresh para a nova API
apiNew.interceptors.response.use(
  (response) => response,
  (error) => handle401Error(error, apiNew)
);

export default api;
export { apiNew };
